import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const WorkNotesCard = ({ workNotes, onAddNote, currentUser }) => {
  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddNote = () => {
    if (newNote?.trim()) {
      onAddNote({
        id: Date.now(),
        content: newNote?.trim(),
        author: currentUser?.name,
        authorId: currentUser?.id,
        timestamp: new Date()?.toISOString(),
        type: 'WORK_NOTE'
      });
      setNewNote('');
      setIsAdding(false);
    }
  };

  const getNoteTypeIcon = (type) => {
    const icons = {
      WORK_NOTE: 'FileText',
      STATUS_CHANGE: 'ArrowRight',
      ASSIGNMENT: 'UserCheck',
      COMPLETION: 'CheckCircle'
    };
    return icons?.[type] || 'FileText';
  };

  const getNoteTypeColor = (type) => {
    const colors = {
      WORK_NOTE: 'text-blue-600',
      STATUS_CHANGE: 'text-green-600',
      ASSIGNMENT: 'text-purple-600',
      COMPLETION: 'text-emerald-600'
    };
    return colors?.[type] || 'text-text-secondary';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date?.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      return date?.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Icon name="MessageSquare" size={20} />
          Arbeitsnotizen
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAdding(!isAdding)}
          iconName={isAdding ? "X" : "Plus"}
          iconPosition="left"
        >
          {isAdding ? 'Abbrechen' : 'Notiz hinzufügen'}
        </Button>
      </div>
      <div className="space-y-4">
        {/* Add New Note */}
        {isAdding && (
          <div className="p-4 border border-border rounded-lg bg-muted/50">
            <div className="space-y-3">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e?.target?.value)}
                placeholder="Arbeitsnotiz eingeben..."
                className="w-full p-3 border border-input rounded-lg resize-none h-24 text-sm"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  variant="default"
                  onClick={handleAddNote}
                  disabled={!newNote?.trim()}
                  iconName="Check"
                  iconPosition="left"
                  size="sm"
                >
                  Speichern
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setNewNote('');
                    setIsAdding(false);
                  }}
                  size="sm"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Work Notes List */}
        <div className="space-y-3">
          {workNotes?.length === 0 ? (
            <div className="text-center py-8 text-text-secondary">
              <Icon name="MessageSquare" size={48} className="mx-auto mb-3 opacity-50" />
              <p>Noch keine Arbeitsnotizen vorhanden</p>
              <p className="text-sm">Fügen Sie die erste Notiz hinzu, um den Arbeitsfortschritt zu dokumentieren.</p>
            </div>
          ) : (
            workNotes?.map((note) => (
              <div key={note?.id} className="flex gap-3 p-4 border border-border rounded-lg">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full overflow-hidden">
                    <Image
                      src={`https://randomuser.me/api/portraits/men/${note?.authorId % 50}.jpg`}
                      alt={note?.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-text-primary">{note?.author}</span>
                    <Icon 
                      name={getNoteTypeIcon(note?.type)} 
                      size={14} 
                      className={getNoteTypeColor(note?.type)} 
                    />
                    <span className="text-sm text-text-secondary">
                      {formatTimestamp(note?.timestamp)}
                    </span>
                  </div>
                  
                  <p className="text-text-primary text-sm leading-relaxed whitespace-pre-wrap">
                    {note?.content}
                  </p>
                  
                  {note?.attachments && note?.attachments?.length > 0 && (
                    <div className="mt-2 flex gap-2">
                      {note?.attachments?.map((attachment, index) => (
                        <div key={index} className="flex items-center gap-1 text-xs text-primary">
                          <Icon name="Paperclip" size={12} />
                          <span>{attachment?.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Quick Actions */}
        {workNotes?.length > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="flex gap-2 text-sm">
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                iconPosition="left"
              >
                Notizen exportieren
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Printer"
                iconPosition="left"
              >
                Drucken
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkNotesCard;